// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Estoque/MovimentacaoForm.tsx
//  Novo lançamento de movimentação de estoque
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';
import { useMovimentacao, useTiposMovimento } from '@/features/estoque/hooks/useMovimentacao';
import { useLocalEstoqueCombo } from '@/features/estoque/hooks/useLocalEstoque';
import { TIPOS_MOVIMENTO } from '@/features/estoque/types';
import type { MovimentacaoProdutoRequest } from '@/features/estoque/types';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

type ItemRow = MovimentacaoProdutoRequest & { _key: number };

const hoje = () => new Date().toISOString().slice(0, 10);

const MovimentacaoForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ?tipo=1|2 vem dos atalhos Entrada/Saída da tela principal de Estoque.
  const [tipoMovimentoId, setTipoMovimentoId] = useState(() => Number(searchParams.get('tipo')) || 1);
  const [dtMovimento,     setDtMovimento]     = useState(hoje());
  const [numDocumento,    setNumDocumento]    = useState('');
  const [justificativa,   setJustificativa]   = useState('');
  const [itens,           setItens]           = useState<ItemRow[]>([]);
  const [nextKey,         setNextKey]         = useState(1);

  const { create, isCreating }  = useMovimentacao();
  const { data: tipos }         = useTiposMovimento();
  const { data: locais }        = useLocalEstoqueCombo();

  const addItem = () => {
    setNextKey((k) => {
      setItens((prev) => [
        ...prev,
        { _key: k, subProdutoId: 0, qtdEntrada: 0, qtdSaida: 0, valorUnitario: 0, localEntradaId: 0, localSaidaId: 0 },
      ]);
      return k + 1;
    });
  };

  const removeItem = (key: number) => setItens((p) => p.filter((r) => r._key !== key));

  const updateItem = (key: number, field: keyof MovimentacaoProdutoRequest, value: number) =>
    setItens((p) => p.map((r) => r._key === key ? { ...r, [field]: value } : r));

  const setItemProduto = (key: number, subProdutoId: number) =>
    setItens((p) => p.map((r) => r._key === key ? { ...r, subProdutoId } : r));

  const isEntrada      = tipoMovimentoId === 1 || tipoMovimentoId === 4;
  const isSaida        = tipoMovimentoId === 2 || tipoMovimentoId === 5;
  const isTransferencia = tipoMovimentoId === 3;

  const handleSubmit = async () => {
    if (itens.length === 0) return;
    if (itens.some((i) => i.subProdutoId === 0)) return;

    await create({
      tipoMovimentoId,
      dtMovimento,
      numDocumento: numDocumento || undefined,
      justificativa: justificativa || undefined,
      itens: itens.map(({ _key: _k, ...rest }) => rest),
    });

    navigate('/estoque/movimentacoes');
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/estoque/movimentacoes')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Nova Movimentação de Estoque</h1>
          <p className="text-muted-foreground">Registre uma entrada, saída, transferência ou ajuste</p>
        </div>
      </div>

      {/* Cabeçalho */}
      <Card>
        <CardHeader>
          <CardTitle>Identificação</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Tipo de Movimento *</Label>
            <Select value={String(tipoMovimentoId)} onValueChange={(v) => setTipoMovimentoId(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(tipos ?? TIPOS_MOVIMENTO).map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>{t.descricao}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Data do Movimento *</Label>
            <Input type="date" value={dtMovimento} onChange={(e) => setDtMovimento(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Nº Documento</Label>
            <Input placeholder="NF, ordem, etc." value={numDocumento} onChange={(e) => setNumDocumento(e.target.value)} />
          </div>
          <div className="md:col-span-2 lg:col-span-3 space-y-1">
            <Label>Justificativa</Label>
            <Textarea
              placeholder="Motivo / observações"
              rows={2}
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Itens da Movimentação</CardTitle>
            <Button size="sm" variant="outline" className="gap-2" onClick={addItem}>
              <Plus className="h-4 w-4" />Adicionar Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {itens.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Nenhum item adicionado. Clique em "Adicionar Item".</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[280px]">Produto</TableHead>
                    {(isEntrada || isTransferencia) && <TableHead className="text-right">Qtd. Entrada</TableHead>}
                    {(isSaida   || isTransferencia) && <TableHead className="text-right">Qtd. Saída</TableHead>}
                    <TableHead className="text-right">Vlr. Unitário</TableHead>
                    {(isEntrada || isTransferencia) && <TableHead>Local Entrada</TableHead>}
                    {(isSaida   || isTransferencia) && <TableHead>Local Saída</TableHead>}
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itens.map((item) => (
                    <TableRow key={item._key}>
                      <TableCell>
                        <ProdutoSearchCombobox
                          value={item.subProdutoId || undefined}
                          onChange={(id) => setItemProduto(item._key, id)}
                          placeholder="Buscar produto..."
                        />
                      </TableCell>
                      {(isEntrada || isTransferencia) && (
                        <TableCell>
                          <Input
                            type="number"
                            min={0}
                            step={0.001}
                            className="text-right w-24"
                            value={item.qtdEntrada}
                            onChange={(e) => updateItem(item._key, 'qtdEntrada', Number(e.target.value))}
                          />
                        </TableCell>
                      )}
                      {(isSaida || isTransferencia) && (
                        <TableCell>
                          <Input
                            type="number"
                            min={0}
                            step={0.001}
                            className="text-right w-24"
                            value={item.qtdSaida}
                            onChange={(e) => updateItem(item._key, 'qtdSaida', Number(e.target.value))}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Input
                          type="number"
                          min={0}
                          step={0.01}
                          className="text-right w-28"
                          value={item.valorUnitario}
                          onChange={(e) => updateItem(item._key, 'valorUnitario', Number(e.target.value))}
                        />
                      </TableCell>
                      {(isEntrada || isTransferencia) && (
                        <TableCell>
                          <Select
                            value={item.localEntradaId > 0 ? String(item.localEntradaId) : ''}
                            onValueChange={(v) => updateItem(item._key, 'localEntradaId', Number(v))}
                          >
                            <SelectTrigger className="w-40"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                            <SelectContent>
                              {locais?.map((l) => (
                                <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      )}
                      {(isSaida || isTransferencia) && (
                        <TableCell>
                          <Select
                            value={item.localSaidaId > 0 ? String(item.localSaidaId) : ''}
                            onValueChange={(v) => updateItem(item._key, 'localSaidaId', Number(v))}
                          >
                            <SelectTrigger className="w-40"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                            <SelectContent>
                              {locais?.map((l) => (
                                <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      )}
                      <TableCell>
                        <Button size="icon" variant="ghost" onClick={() => removeItem(item._key)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/estoque/movimentacoes')}>Cancelar</Button>
        <Button
          className="gap-2"
          onClick={handleSubmit}
          disabled={isCreating || itens.length === 0 || itens.some((i) => i.subProdutoId === 0)}
        >
          <Save className="h-4 w-4" />
          {isCreating ? 'Salvando...' : 'Salvar Movimentação'}
        </Button>
      </div>
    </div>
  );
};

export default MovimentacaoForm;
